import { LogDoc, LogOrder, OrderDoc, OrderProduct, OrderStatusCode } from '@gooduncles/gu-app-schema';
import { message } from 'antd';
import { FC, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { textSort } from 'src/utils/common';
import { safeBottomForFooterButton } from 'src/utils/constants/ui-constants';
import { orderStatusKr } from 'src/utils/constants/ui-constants';
import { formatDate } from 'src/utils/date-util';
import { calculateIssueTasksAmount } from 'src/utils/issue-task-util';
import { logger } from 'src/utils/logger';
import { getOrderProductsDiffs, loadPrevOrderProducts } from 'src/utils/order-util';

import useCart from 'src/redux/hooks/useCart';
import { selectUser } from 'src/redux/slices/auth';
import { selectSubmiited } from 'src/redux/slices/order';
import { useAppSelector } from 'src/redux/store';

import useIssueTasksByIds from 'src/hooks/useIssueTasksByIds';
import useStoreIssuesByIds from 'src/hooks/useStoreIssuesByIds';

import { useApp } from 'src/stores/app-context';

import FooterButton from 'src/components/atoms/Button/FooterButton';
import EmptyList from 'src/components/atoms/EmptyList/EmptyList';
import IssueTaskItem from 'src/components/atoms/IssueTaskItem/IssueTaskItem';
import OrderInfo from 'src/components/atoms/OrderInfo/OrderInfo';
import OrderItem from 'src/components/atoms/OrderItem/OrderItem';
import StoreIssueItem from 'src/components/atoms/StoreIssueItem/StoreIssueItem';
import TopNavigationBar from 'src/components/atoms/TopNavigationBar/TopNavigationBar';

import classes from './OrderDetail.module.scss';

type OrderDetailProps = {
  order?: OrderDoc;
  logs?: LogDoc<LogOrder>[];
};

const OrderDetail: FC<OrderDetailProps> = ({ order, logs }) => {
  const { setShowLoading } = useApp();
  const user = useAppSelector(selectUser, (prev, next) => prev?._id === next?._id);
  const submittedOrder = useAppSelector(selectSubmiited);
  const [searchParams] = useSearchParams();
  const issueIds = searchParams.getAll('issue-id');
  const taskIds = searchParams.getAll('task-id');
  const { storeIssues } = useStoreIssuesByIds(issueIds.length > 0 ? issueIds : null);
  const { issueTasks } = useIssueTasksByIds(taskIds.length > 0 ? taskIds : null);
  const { onSetProducts } = useCart();
  const navigate = useNavigate();
  const orderProducts = getOrderProductsDiffs(order?.products, logs ?? []);

  const issuesTotalAmount = storeIssues
    ? storeIssues.reduce((acc, cur) => acc + ((cur.supplyPrice ?? 0) + (cur.tax ?? 0)) * (cur.volume ?? 1), 0)
    : null;

  const issueTasksTotalAmount = calculateIssueTasksAmount(issueTasks);

  const goToCartWithNewCart = useCallback(
    async (prevOrderProducts: OrderProduct[]) => {
      if (submittedOrder) {
        logger.logCommerce(`이전 주문 불러오기 실패 - 이미 생성한 주문이 있습니다.`);
        message.error('이미 생성한 주문이 있습니다.');
        return;
      } else {
        logger.logCommerce(`주문 목록에 있는 주문 불러오기 - orderId: ${order?._id}, orderDate: ${order?.orderDate}`);

        setShowLoading(true);
        const products = await loadPrevOrderProducts(prevOrderProducts);
        if (products) {
          onSetProducts(products);
          navigate('/shopping-cart');
        }
        setShowLoading(false);
      }
    },
    [navigate, onSetProducts, order?._id, order?.orderDate, setShowLoading, submittedOrder]
  );

  if (!order) {
    return <EmptyList message='삭제되었거나, 잘못된 주문입니다.' />;
  }

  const title =
    order.orderStatus === OrderStatusCode.DELIVERED
      ? formatDate(order.date ?? order.deliveryDate, 'L월 d일 (EEEEEE)')
      : orderStatusKr[order.orderStatus];

  return (
    <div>
      <TopNavigationBar title={title ?? '오류가 있습니다.'} leftButton />
      <OrderInfo
        order={order}
        paidAmount={
          (issuesTotalAmount ? order.paidAmount + issuesTotalAmount : order.paidAmount) + (issueTasksTotalAmount ?? 0)
        }
      />
      <section className={classes.orderDetail}>
        {orderProducts &&
          orderProducts
            .sort((a, b) => textSort(a.fullName, b.fullName))
            ?.map((product) => {
              const orderProduct = order?.products?.find((op) => op.productId === product.productId);
              return <OrderItem key={product.productId} orderProduct={product} volume={orderProduct?.volume} />;
            })}
        {storeIssues && storeIssues.length > 0 && (
          <>
            <p className={classes.issueHeader}>※ 특이사항</p>
            {storeIssues?.map((storeIssue) => {
              return <StoreIssueItem key={storeIssue._id} storeIssue={storeIssue} />;
            })}
            <p className={classes.issueFooter}>안내된 내용은 다음 정산에 포함됩니다.</p>
          </>
        )}
        {issueTasks && issueTasks.length > 0 && (
          <>
            <p className={classes.issueHeader}>※ 이슈사항</p>
            {issueTasks?.map((issueTask) => {
              return <IssueTaskItem key={issueTask._id} issueTask={issueTask} />;
            })}
          </>
        )}
      </section>

      {user && order.products?.length > 0 && (
        <FooterButton
          text='이 주문 그대로 장바구니에 담기'
          onClick={() => goToCartWithNewCart(order.products)}
          bottom={safeBottomForFooterButton}
        />
      )}
    </div>
  );
};

export default OrderDetail;
